'use strict';
/**
 * Animated 3D tank for the start screen ("hero" visual) - the "Rookie"
 * imported GLB from tank-concepts/, run in driveMode (see gltf-tank-kit.js):
 * creeps forward/back, pivot-turns left/right (sometimes far enough to show
 * its back), occasionally fires the cannon, and blinks its googly eyes.
 * Everything is decorative - if WebGL is unavailable or the model fails to
 * load this silently no-ops and the plain title/logo below still works.
 */
import * as THREE from 'three';
import { buildTank as buildRookieTank } from './tank-concepts/tank-08-rookie.js';

// Mirrors TANK_COLORS in client.js (itself a mirror of server/index.js's
// COLORS) - duplicated here, same trade-off client.js's own copy already
// documents, so this module can turn a persisted/picked swatch index into a
// hex without importing the non-module client.js. Shared with
// public/tank-studio.js, which keeps its own identical copy for the same
// reason.
const TANK_COLORS = [
  '#e74c3c', '#3498db', '#2ecc71', '#f1c40f', '#9b59b6',
  '#e67e22', '#1abc9c', '#ff6fa5', '#00d2ff', '#c0392b',
];
const STORAGE_TANK_COLOR = 'panzer_tank_color';

const container = document.getElementById('tank-stage');
const canvas = document.getElementById('tank-canvas');

let mascotTank = null;

function applyColorIndex(t, index) {
  // null = "random" preference (server assigns the color) - nothing sensible
  // to preview, so just leave whatever color is already showing.
  if (!t || index == null) return;
  const hex = TANK_COLORS[index];
  if (!hex) return;
  t.skinMaterials.body.forEach((m) => m.color.set(hex));
}

window.addEventListener('tanks:colorchange', (e) => {
  applyColorIndex(mascotTank, e.detail && e.detail.index);
});

if (container && canvas) {
  initMenuTank(container, canvas).catch((err) => {
    // Decorative only - never break the menu if WebGL/three.js misbehaves.
    console.warn('[menu-tank] disabled:', err);
  });
}

async function initMenuTank(container, canvas) {
  const renderer = new THREE.WebGLRenderer({ canvas, antialias: true, alpha: true });
  renderer.setPixelRatio(Math.min(window.devicePixelRatio || 1, 2));
  if ('outputColorSpace' in renderer) renderer.outputColorSpace = THREE.SRGBColorSpace;
  // alpha:true alone doesn't guarantee a transparent backing buffer - until
  // the first clear/render call, some browsers paint a freshly created WebGL
  // context as opaque white. buildRookieTank() below is async (GLTF fetch),
  // so on a slow connection that white gap can last seconds even though the
  // .ready opacity gate hides it almost instantly on a fast one. Clearing
  // right away closes that gap regardless of connection speed.
  renderer.setClearColor(0x000000, 0);
  renderer.clear();

  const scene = new THREE.Scene();
  const camera = new THREE.PerspectiveCamera(26, 1, 0.1, 50);

  // ---- lights: bright toy-diorama look ----
  scene.add(new THREE.HemisphereLight(0xcfe0ff, 0x2a1f4a, 1.05));
  const key = new THREE.DirectionalLight(0xfff2d6, 1.6);
  key.position.set(4, 6.5, 5);
  scene.add(key);
  const rim = new THREE.DirectionalLight(0x8fb2ff, 0.55);
  rim.position.set(-5, 3, -4);
  scene.add(rim);
  const fill = new THREE.PointLight(0xffffff, 0.35);
  fill.position.set(-2.5, 1.2, 3);
  scene.add(fill);

  // turretScanAmplitude/Speed widened + slowed from the gallery-card default
  // (a small ~8deg idle sway) so the turret can swing well past its own
  // sides toward the back, like it's tracking a target - checked against
  // the camera fit below (the widest swing's reach stays under the model's
  // own rest-pose corner radius, so it doesn't need extra camera margin).
  const tank = await buildRookieTank({ driveMode: true, turretScanAmplitude: 2.0, turretScanSpeed: 0.4 });
  scene.add(tank.root);
  mascotTank = tank;
  try {
    applyColorIndex(tank, JSON.parse(localStorage.getItem(STORAGE_TANK_COLOR)));
  } catch (e) {}

  // ---- frame the camera on the tank (same elevated 3/4 "diorama" angle the
  // hand-built tank used). driveMode turns/translates the whole root, so a
  // plain bounding-box fit (sized for the tank's CURRENT rest pose) would
  // leave it clipping the frame the moment it turns or drives - instead fit
  // a vertical cylinder around the tank's own up-axis: horizontally, the
  // tank can face any yaw, so what matters is its max reach from that axis
  // (R) plus how far the drive state machine can carry it off-center
  // (driveClamp); vertically, only its true height range matters (yaw
  // doesn't change that). Fitting width/height separately like this - each
  // against its actual matching FOV axis - avoids the extra slack a single
  // 3D-diagonal sphere fit would add by conflating depth into the radius.
  const box = new THREE.Box3().setFromObject(tank.root);
  const center = box.getCenter(new THREE.Vector3());
  const size = box.getSize(new THREE.Vector3());
  const cornerR = Math.hypot(size.x, size.z) / 2;
  // matches driveClampX/Z in gltf-tank-kit.js's driveMode state machine.
  const driveClamp = Math.hypot(0.2, 0.16) * Math.max(size.x, size.z);
  const horizR = cornerR + driveClamp + 0.1;
  const halfHeight = size.y / 2 + 0.08;

  const vFovHalf = THREE.MathUtils.degToRad(camera.fov / 2);
  const hFovHalf = Math.atan(Math.tan(vFovHalf) * (container.clientWidth / container.clientHeight || 1.5));
  const distV = halfHeight / Math.tan(vFovHalf);
  const distH = horizR / Math.tan(hFovHalf);
  const fitDist = Math.max(distV, distH) * 1.05;

  const viewDir = new THREE.Vector3(2.75, 2.2, 7.6).normalize();
  camera.position.copy(center).addScaledVector(viewDir, fitDist);
  camera.lookAt(center);

  // ---- responsive sizing ----
  function resize() {
    const w = container.clientWidth;
    const h = container.clientHeight;
    if (!w || !h) return;
    renderer.setSize(w, h, false);
    camera.aspect = w / h;
    camera.updateProjectionMatrix();
  }
  if (window.ResizeObserver) {
    new ResizeObserver(resize).observe(container);
  } else {
    window.addEventListener('resize', resize);
  }
  resize();

  // ---- eyes subtly follow the pointer ----
  const pointer = { x: 0, y: 0 };
  window.addEventListener('pointermove', (e) => {
    pointer.x = (e.clientX / window.innerWidth) * 2 - 1;
    pointer.y = (e.clientY / window.innerHeight) * 2 - 1;
  }, { passive: true });

  const startScreen = document.getElementById('start-screen');
  let lastT = performance.now();
  let revealed = false;
  let rafId = null;

  function frame(now) {
    rafId = requestAnimationFrame(frame);
    const dt = Math.min((now - lastT) / 1000, 0.05);
    lastT = now;
    if (document.hidden) return;
    tank.update(dt, pointer);
    renderer.render(scene, camera);
    // Canvas starts at opacity:0 (see .tank-stage canvas in style.css) so
    // the brief gap between page paint and this first real frame - vendor
    // module fetch/parse, WebGL context creation - never shows as a flash
    // of blank canvas.
    if (!revealed) {
      revealed = true;
      canvas.classList.add('ready');
    }
  }

  function isMenuVisible() {
    return !startScreen || !startScreen.classList.contains('screen-off');
  }
  function startLoop() {
    if (rafId !== null) return;
    lastT = performance.now();
    rafId = requestAnimationFrame(frame);
  }
  function stopLoop() {
    if (rafId === null) return;
    cancelAnimationFrame(rafId);
    rafId = null;
  }

  if (isMenuVisible()) startLoop();

  // Real gameplay hides the start screen via the 'screen-off' class - stop
  // scheduling rAF entirely while it's hidden instead of just skipping the
  // render body inside frame(), so this decorative scene doesn't keep
  // costing a callback every frame for the whole time a match is playing.
  if (startScreen && 'MutationObserver' in window) {
    new MutationObserver(() => {
      if (isMenuVisible()) startLoop(); else stopLoop();
    }).observe(startScreen, { attributes: true, attributeFilter: ['class'] });
  }
}
